import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 max-w-md text-center">
            <AlertTriangle size={32} className="mx-auto text-danger mb-3" />
            <h2 className="text-[18px] font-medium mb-2">Something went wrong</h2>
            <p className="text-[14px] text-text-secondary mb-4">
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = "/projects";
              }}
              className="px-4 py-2 bg-brand text-white rounded-xl text-[16px] font-medium"
            >
              Return to projects
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
